import imageCompression from 'browser-image-compression';
import { needsDecoding, toDisplayableBlob } from '../shared/heicDisplay';
import { OUTPUT_FORMAT_META, encodeCanvas, resolveOutputFormat } from '../shared/imageOutputFormats';

export { isSupportedImageFile, IMAGE_INPUT_ACCEPT } from '../shared/imageInputFormats';

/**
 * The longest side a compressed picture may keep. `null` keeps the original
 * size, and is the default: resizing changes the picture itself, which is more
 * than someone asking for a smaller file has asked for. Quality alone already
 * takes most of the weight off a phone photo; the sizes below are here for when
 * the dimensions are the problem.
 */
export const MAX_DIMENSION_OPTIONS = [
    { id: 'original', value: null },
    { id: '3840', value: 3840 },
    { id: '2560', value: 2560 },
    { id: '1920', value: 1920 },
    { id: '1280', value: 1280 },
    { id: '1024', value: 1024 },
    { id: '800', value: 800 },
];

export const DEFAULT_COMPRESS_SETTINGS = {
    quality: 75,
    maxDimension: 'original',
    preserveExif: false,
    keepOriginalName: false,
};

/**
 * HEIC, TIFF and SVG are decoded first — the compressor draws through a canvas,
 * which can read none of them (HEIC outside Safari, TIFF anywhere, and an SVG
 * only once it has been given a size). Goes through the same cache the previews
 * use, so a picture already shown in the grid is not decoded a second time.
 */
async function prepareFile(file) {
    if (!needsDecoding(file)) return file;
    const blob = await toDisplayableBlob(file);
    if (blob === file) return file; // decoding failed — let the browser try
    const base = file.name.replace(/\.[^.]+$/, '');
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    return new File([blob], `${base}.${ext}`, { type: blob.type });
}

const EXT_BY_TYPE = {
    'image/png': 'png',
    'image/webp': 'webp',
    'image/avif': 'avif',
    'image/bmp': 'bmp',
    'image/gif': 'gif',
    'image/jpeg': 'jpg',
};

function extensionForBlob(blob) {
    return EXT_BY_TYPE[blob.type] || 'jpg';
}

/**
 * `photo.jpg` → `photo-compressed.jpg`, or `photo.jpg` with "keep original name".
 * The extension always follows what was actually written, so a format change
 * never leaves a PNG called .jpg.
 */
export function compressedImageName(file, blob, { keepOriginalName = false } = {}) {
    const base = file.name.replace(/\.[^.]+$/, '');
    const ext = extensionForBlob(blob);
    return keepOriginalName ? `${base}.${ext}` : `${base}-compressed.${ext}`;
}

// Map a UI format id to an output MIME type. 'current' keeps the source format.
export function formatMimeType(format) {
    switch (format) {
        case 'webp': return 'image/webp';
        case 'jpg':  return 'image/jpeg';
        case 'png':  return 'image/png';
        case 'avif': return 'image/avif';
        case 'bmp':  return 'image/bmp';
        default:     return null; // 'current' → let the library keep the input type
    }
}

/** Feature-detect whether the browser can ENCODE a given image MIME type. */
export function canEncodeFormat(mime) {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 1;
        return canvas.toDataURL(mime).startsWith(`data:${mime}`);
    } catch {
        return false;
    }
}

/**
 * EXIF can only be carried JPEG → JPEG: the compressor copies the original APP1
 * segment into the new file, and no other output format has a place for it.
 * HEIC is decoded to JPEG first, which already loses its metadata.
 */
export function canPreserveExif(file, format) {
    const outputIsJpeg = !format || format === 'original' || format === 'current' || format === 'jpg';
    const inputIsJpeg = /jpe?g/i.test(file?.type || '') || /\.jpe?g$/i.test(file?.name || '');
    return outputIsJpeg && inputIsJpeg;
}

/**
 * AVIF goes around the compressor: that library re-encodes through the canvas,
 * which cannot write AVIF in Chrome (it quietly hands back a PNG). Scale here,
 * then let the shared encoder write real AVIF through WebAssembly.
 */
async function compressToAvif(file, quality, maxDimension) {
    const bitmap = await createImageBitmap(file);
    const scale = maxDimension ? Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height)) : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const { blob } = await encodeCanvas(canvas, 'avif', { quality: quality / 100 });
    return blob;
}

// Lowest quality the size search will accept before it shrinks the picture instead —
// a slightly smaller image reads better than a blocky one.
const TARGET_QUALITY_FLOOR = 0.45;
const TARGET_QUALITY_CEIL = 0.92;
const TARGET_MAX_ATTEMPTS = 6;
const TARGET_MIN_SIDE = 48;

/**
 * Compress to fit UNDER a file size. Lossy formats first search the quality dial for
 * the best quality that fits; when even the floor is too big the picture is scaled
 * down (the scale is estimated from how far over it was, so it converges in a couple
 * of rounds) and the search repeats. Lossless output (PNG) can only be scaled.
 *
 * Best effort: when nothing fits, the smallest attempt is returned.
 */
async function fitUnderSize(file, { targetBytes, format, maxDimension }) {
    const prepared = await prepareFile(file);
    const outFormat = resolveOutputFormat(format, prepared);
    const lossy = OUTPUT_FORMAT_META[outFormat]?.lossy ?? false;
    const bitmap = await createImageBitmap(prepared);

    let scale = maxDimension ? Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height)) : 1;
    const sourceFormat = resolveOutputFormat('original', prepared);
    // Already small enough, in the format asked for, untouched: nothing to gain.
    if (prepared.size <= targetBytes && sourceFormat === outFormat && scale === 1) {
        bitmap.close?.();
        return prepared;
    }

    const drawAt = (s) => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * s));
        canvas.height = Math.max(1, Math.round(bitmap.height * s));
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        return canvas;
    };
    const encode = async (canvas, quality) => (await encodeCanvas(canvas, outFormat, { quality })).blob;
    // AVIF is written in WebAssembly and takes seconds per pass, so it gets fewer probes.
    const probes = outFormat === 'avif' ? 4 : 6;

    let smallest = null;
    try {
        for (let attempt = 0; attempt < TARGET_MAX_ATTEMPTS; attempt += 1) {
            const canvas = drawAt(scale);

            if (!lossy) {
                const blob = await encode(canvas);
                if (!smallest || blob.size < smallest.size) smallest = blob;
                if (blob.size <= targetBytes) return blob;
                scale *= Math.max(0.3, Math.sqrt(targetBytes / blob.size) * 0.97);
            } else {
                const best = await encode(canvas, TARGET_QUALITY_CEIL);
                if (best.size <= targetBytes) return best;
                const floor = await encode(canvas, TARGET_QUALITY_FLOOR);
                if (!smallest || floor.size < smallest.size) smallest = floor;

                if (floor.size <= targetBytes) {
                    // Somewhere between: highest quality that still fits.
                    let fit = floor;
                    let lo = TARGET_QUALITY_FLOOR;
                    let hi = TARGET_QUALITY_CEIL;
                    for (let i = 0; i < probes; i += 1) {
                        const mid = (lo + hi) / 2;
                        const blob = await encode(canvas, mid);
                        if (blob.size <= targetBytes) { fit = blob; lo = mid; } else { hi = mid; }
                    }
                    return fit;
                }
                scale *= Math.max(0.3, Math.sqrt(targetBytes / floor.size) * 0.97);
            }

            if (Math.min(bitmap.width, bitmap.height) * scale < TARGET_MIN_SIDE) break;
        }
    } finally {
        bitmap.close?.();
    }
    return smallest;
}

/** A 1×1 JPEG — the smallest stand-in for measuring how much room EXIF takes. */
function tinyJpegFile() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(new File([blob], 'tiny.jpg', { type: 'image/jpeg' })), 'image/jpeg', 0.9);
    });
}

/**
 * `fitUnderSize`, optionally carrying the original's EXIF (camera, date, location) over.
 * The EXIF block can be tens of KB, so its size is reserved up front: the picture is fitted
 * under `target - EXIF` and the EXIF is added last, keeping the whole file under the target.
 * JPEG → JPEG only, as elsewhere (see canPreserveExif).
 */
async function compressToTargetSize(file, { targetBytes, format, maxDimension, preserveExif }) {
    const keepExif = Boolean(preserveExif) && canPreserveExif(file, format);
    if (!keepExif) return fitUnderSize(file, { targetBytes, format, maxDimension });

    const tiny = await tinyJpegFile();
    const overhead = Math.max(0, (await imageCompression.copyExifWithoutOrientation(file, tiny)).size - tiny.size);
    // Never let EXIF eat more than half the budget — the picture still has to be worth keeping.
    const budget = Math.max(targetBytes - overhead, Math.round(targetBytes * 0.5));

    const fitted = await fitUnderSize(file, { targetBytes: budget, format, maxDimension });
    if (fitted === file) return file; // already small enough, untouched — its own EXIF is still in it
    const asFile = new File([fitted], file.name, { type: fitted.type || 'image/jpeg' });
    return imageCompression.copyExifWithoutOrientation(file, asFile);
}

/**
 * Compress an image file in the browser.
 * @param {File} file
 * @param {{ quality?: number, maxDimension?: string, preserveExif?: boolean, format?: string, targetBytes?: number }} options
 *   quality — 1…100; maxDimension — an id from MAX_DIMENSION_OPTIONS;
 *   targetBytes — fit under this many bytes instead of using `quality`
 */
export async function compressImageFile(file, options = {}) {
    const fileType = formatMimeType(options.format) || undefined;
    const quality = Math.min(100, Math.max(1, Number(options.quality) || DEFAULT_COMPRESS_SETTINGS.quality));
    const maxDimension = MAX_DIMENSION_OPTIONS.find((opt) => opt.id === options.maxDimension)?.value ?? null;
    // A file-size target replaces the quality dial altogether.
    const targetBytes = Number(options.targetBytes) || 0;
    if (targetBytes > 0) {
        return compressToTargetSize(file, {
            targetBytes,
            format: options.format,
            maxDimension,
            preserveExif: options.preserveExif,
        });
    }

    const prepared = await prepareFile(file);

    // AVIF out (picked, or "original" on an AVIF upload) cannot go through the
    // compressor — see compressToAvif.
    const wantsAvif = options.format === 'avif'
        || ((!options.format || options.format === 'original') && /avif/i.test(prepared.type || ''));
    if (wantsAvif) return compressToAvif(prepared, quality, maxDimension);

    return imageCompression(prepared, {
        // No size ceiling: the quality dial is what decides the file size.
        maxSizeMB: Number.POSITIVE_INFINITY,
        ...(maxDimension ? { maxWidthOrHeight: maxDimension } : {}),
        useWebWorker: true,
        initialQuality: quality / 100,
        preserveExif: Boolean(options.preserveExif) && canPreserveExif(file, options.format),
        ...(fileType ? { fileType } : {}),
    });
}

export { formatFileSize } from '../../pdf/compress/compressPdfEngine';
